import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Badge,
  Tooltip,
  Alert,
  Divider,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useLeadStore } from '../stores/leadStore';
import { Lead } from '../services/leadService';
import {
  Search,
  FilterList,
  Download,
  MoreVert,
  Phone,
  Message,
  Person,
  DirectionsCar,
  Schedule,
  Add,
  TrendingUp,
  CheckCircle,
  AutoMode,
  Assignment,
  Star,
  AccessTime,
  Timeline,
  PlayArrow,
  Pause,
  Edit,
  Visibility,
  Home,
  NavigateNext,
} from '@mui/icons-material';


interface QualificationStage {
  id: number;
  name: string;
  description: string;
  automationRules: string[];
  requiredActions: string[];
  avgDuration: string;
}

const qualificationStages: QualificationStage[] = [
  {
    id: 0,
    name: 'Initial Contact',
    description: 'New lead in system, awaiting first contact',
    automationRules: ['Send welcome message', 'Collect basic info', 'Schedule follow-up'],
    requiredActions: ['Verify contact info', 'Understand interest'],
    avgDuration: '1-2 days',
  },
  {
    id: 1,
    name: 'Engagement',
    description: 'Active conversation with AI bot or agent',
    automationRules: ['Ask qualifying questions', 'Assess buying intent', 'Schedule test drive if interested'],
    requiredActions: ['Qualify budget', 'Determine timeline', 'Identify decision makers'],
    avgDuration: '3-5 days',
  },
  {
    id: 2,
    name: 'Qualified',
    description: 'Lead meets criteria, ready for sales process',
    automationRules: ['Assign to agent', 'Schedule appointment', 'Send vehicle info'],
    requiredActions: ['Confirm interest', 'Set appointment', 'Prepare presentation'],
    avgDuration: '1-2 days',
  },
  {
    id: 3,
    name: 'Assigned',
    description: 'Lead assigned to sales agent for direct handling',
    automationRules: ['Monitor progress', 'Send reminders', 'Track interactions'],
    requiredActions: ['Conduct needs analysis', 'Present options', 'Negotiate terms'],
    avgDuration: '1-3 weeks',
  },
  {
    id: 4,
    name: 'Converted',
    description: 'Successfully closed sale',
    automationRules: ['Send congratulations', 'Schedule delivery', 'Request feedback'],
    requiredActions: ['Process paperwork', 'Coordinate delivery', 'Follow up'],
    avgDuration: '1-2 weeks',
  },
];


const Leads: React.FC = () => {
  const { leads, fetchLeads, isLoading } = useLeadStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    fetchLeads();
  }, []);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [showStageDialog, setShowStageDialog] = useState(false);
  const [selectedStage, setSelectedStage] = useState<QualificationStage | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showWorkflow, setShowWorkflow] = useState(false);

  const stageMap: Record<Lead['stage'] | 'assigned', string> = {
    unassigned: 'New',
    assigned: 'Assigned',
    new: 'Engaging',
    contacted: 'Qualified',
    appointment: 'Converted',
    'no-interest': 'Lost'
  };

  const resolveStatusLabel = (lead: Lead): string => {
    switch (lead.originalStatus) {
      case 'qualified':
        return 'Qualified';
      case 'engaging':
        return 'Engaging';
      case 'assigned':
        return 'Assigned';
      case 'in_contact':
        return 'In Contact';
      case 'converted':
        return 'Converted';
      case 'lost':
      case 'not_interested':
        return 'Lost';
      case 'queued':
      case 'potential':
      case 'unassigned':
      case 'recycled':
        return 'New';
      default:
        break;
    }

    if (lead.stage === 'unassigned' && lead.interestLevel) {
      return 'Qualified';
    }

    return stageMap[lead.stage] || 'New';
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return '#f44336';
      case 'medium':
        return '#ff9800';
      case 'low':
        return '#4caf50';
      default:
        return '#9e9e9e';
    }
  };

  const getPipelineStats = () => {
    const stats = leads.reduce((acc, lead) => {
      const status = resolveStatusLabel(lead);
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      total: leads.length,
      new: stats['New'] || 0,
      engaging: stats['Engaging'] || 0,
      qualified: stats['Qualified'] || 0,
      assigned: stats['Assigned'] || 0,
      converted: stats['Assigned'] || 0,
    };
  };

  const getConversionRate = () => {
    const stats = getPipelineStats();
    return stats.total > 0 ? ((stats.converted / stats.total) * 100).toFixed(1) : '0';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'New':
        return { bg: '#FFF3E0', color: '#FF9800' };
      case 'Engaging':
        return { bg: '#E3F2FD', color: '#2196F3' };
      case 'Qualified':
        return { bg: '#E8F5E9', color: '#4CAF50' };
      case 'Assigned':
        return { bg: '#F3E5F5', color: '#9C27B0' };
      case 'Converted':
        return { bg: '#E0F7FA', color: '#00BCD4' };
      default:
        return { bg: '#F5F5F5', color: '#616161' };
    }
  };


  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, lead: Lead) => {
    setAnchorEl(event.currentTarget);
    setSelectedLead(lead);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedLead(null);
  };

  const columns: GridColDef[] = [
    {
      field: 'customerName',
      headerName: 'Lead',
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: '#1a237e' }}>
            {params.row.customerName ? params.row.customerName.charAt(0) : '?'}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="500">
              {params.row.customerName || 'Unknown'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.phone || ''}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params: GridRenderCellParams) => {
        const colors = getStatusColor(params.value);
        const lead = params.row as Lead;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={params.value}
              size="small"
              sx={{
                bgcolor: colors.bg,
                color: colors.color,
                fontWeight: 600,
              }}
            />
            {lead.automationActive && (
              <Tooltip title="Automation Active">
                <AutoMode sx={{ fontSize: 16, color: '#4CAF50' }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Star sx={{ fontSize: 16, mr: 0.5, color: getPriorityColor(params.value) }} />
          <Typography variant="caption" sx={{ color: getPriorityColor(params.value), fontWeight: 600 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'interest',
      headerName: 'Interest',
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <DirectionsCar sx={{ fontSize: 16, mr: 1, color: '#616161' }} />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: 'channel',
      headerName: 'Channel',
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const getChannelIcon = (channel: string) => {
          switch (channel) {
            case 'whatsapp': return <Message sx={{ fontSize: 16, mr: 0.5, color: '#25D366' }} />;
            case 'sms': return <Message sx={{ fontSize: 16, mr: 0.5, color: '#1976d2' }} />;
            case 'phone': return <Phone sx={{ fontSize: 16, mr: 0.5, color: '#616161' }} />;
            default: return null;
          }
        };
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {getChannelIcon(params.value)}
            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
              {params.value || 'Unknown'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'assignedTo',
      headerName: 'Assigned To',
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {params.value && (
            <Person sx={{ fontSize: 16, mr: 1, color: '#616161' }} />
          )}
          <Typography
            variant="body2"
            color={!params.value ? 'text.secondary' : 'text.primary'}
          >
            {params.value || 'Unassigned'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'lastContact',
      headerName: 'Last Contact',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Schedule sx={{ fontSize: 16, mr: 1, color: '#616161' }} />
          <Typography variant="body2" color="text.secondary">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <IconButton size="small" sx={{ mr: 1 }}>
            <Phone fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={{ mr: 1 }}>
            <Message fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => handleMenuClick(e, params.row as Lead)}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const filteredLeads = leads
    .map(lead => ({
      ...lead,
      status: resolveStatusLabel(lead)
    }))
    .filter(lead => {
      const matchesSearch = lead.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.vehicle.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = filterStatus === 'all' || lead.status === filterStatus;

      return matchesSearch && matchesStatus;
    });

  const pipelineStats = getPipelineStats();

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          color="inherit"
          href="/dashboard"
          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          <Home sx={{ mr: 0.5 }} fontSize="inherit" />
          Dashboard
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          Lead Management
        </Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Lead Management & Qualification
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Timeline />}
            onClick={() => setShowWorkflow(true)}
            sx={{ borderColor: '#1a237e', color: '#1a237e' }}
          >
            View Workflow
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{
              bgcolor: '#4CAF50',
              '&:hover': { bgcolor: '#43A047' },
            }}
          >
            Add Lead
          </Button>
        </Box>
      </Box>

      {/* Pipeline Overview */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <TrendingUp sx={{ mr: 1, color: '#1a237e' }} />
            Sales Pipeline Overview
          </Typography>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={6} md={2}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {pipelineStats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Leads
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={2}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#FF9800' }} fontWeight="bold">
                  {pipelineStats.new}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  New
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={2}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#2196F3' }} fontWeight="bold">
                  {pipelineStats.engaging}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Engaging
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={2}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#4CAF50' }} fontWeight="bold">
                  {pipelineStats.qualified}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Qualified
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={2}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#9C27B0' }} fontWeight="bold">
                  {pipelineStats.assigned}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Assigned
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={2}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#00BCD4' }} fontWeight="bold">
                  {pipelineStats.converted}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Converted
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Conversion Rate: <strong>{getConversionRate()}%</strong> |
              Active Automations: <strong>{leads.length}</strong>
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Number(getConversionRate())}
              sx={{ width: 200, height: 8, borderRadius: 1 }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Filters and Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="New">New</MenuItem>
              <MenuItem value="Engaging">Engaging</MenuItem>
              <MenuItem value="Qualified">Qualified</MenuItem>
              <MenuItem value="Assigned">Assigned</MenuItem>
              <MenuItem value="Converted">Converted</MenuItem>
              <MenuItem value="Lost">Lost</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            sx={{ borderColor: '#E0E0E0' }}
          >
            More Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            sx={{ borderColor: '#E0E0E0' }}
          >
            Export
          </Button>
        </Box>
      </Paper>

      {/* Data Grid */}
      <Paper sx={{ height: 600 }}>
        <DataGrid
          rows={filteredLeads}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 }
            }
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          checkboxSelection
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              bgcolor: '#FAFAFA',
            },
          }}
        />
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>
          <Visibility sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <Assignment sx={{ mr: 1 }} />
          Assign Agent
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <Edit sx={{ mr: 1 }} />
          Change Status
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          {selectedLead?.automationActive ? <Pause sx={{ mr: 1 }} /> : <PlayArrow sx={{ mr: 1 }} />}
          {selectedLead?.automationActive ? 'Pause Automation' : 'Start Automation'}
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <Timeline sx={{ mr: 1 }} />
          View Stage History
        </MenuItem>
      </Menu>

      {/* Workflow Dialog */}
      <Dialog open={showWorkflow} onClose={() => setShowWorkflow(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Lead Qualification Workflow</DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Automated qualification process with AI-driven progression through sales stages
          </Typography>

          <Stepper activeStep={2} alternativeLabel sx={{ mb: 4 }}>
            {qualificationStages.map((stage) => (
              <Step key={stage.id}>
                <StepLabel
                  onClick={() => setSelectedStage(stage)}
                  sx={{ cursor: 'pointer' }}
                >
                  {stage.name}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {selectedStage && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {selectedStage.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {selectedStage.description}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Average Duration: {selectedStage.avgDuration}
                </Typography>

                <Grid container spacing={3} sx={{ mt: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      <AutoMode sx={{ mr: 1, fontSize: 16 }} />
                      Automation Rules
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                      {selectedStage.automationRules.map((rule, index) => (
                        <li key={index}>
                          <Typography variant="body2">{rule}</Typography>
                        </li>
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      <CheckCircle sx={{ mr: 1, fontSize: 16 }} />
                      Required Actions
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                      {selectedStage.requiredActions.map((action, index) => (
                        <li key={index}>
                          <Typography variant="body2">{action}</Typography>
                        </li>
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>AI Automation Features:</strong> Leads automatically progress through stages based on
              conversation analysis, interest scoring, and behavior patterns. Manual override available at any time.
            </Typography>
          </Alert>

          <Typography variant="h6" gutterBottom>
            Automation Performance
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">85%</Typography>
                <Typography variant="body2">Qualification Accuracy</Typography>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#4CAF50' }}>2.3x</Typography>
                <Typography variant="body2">Faster Processing</Typography>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#FF9800' }}>24/7</Typography>
                <Typography variant="body2">Continuous Operation</Typography>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowWorkflow(false)}>Close</Button>
          <Button variant="contained" sx={{ bgcolor: '#1a237e' }}>
            Configure Workflow
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Leads;
